import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Video, Mail, ShieldCheck, Clock, Phone, ArrowLeft } from "lucide-react";
import PhoenixImage from "../components/PhoenixImage";
import { CalEmbed } from "../components/CalEmbed";

const CONTACT = "rise@whywerecoverlives.com";

const Facts = () => (
  <ul className="grid sm:grid-cols-3 gap-3 text-sm" data-testid="book-facts">
    {[[Clock, "30 minutes", "Tue, Wed & Thu · 10am–4pm Pacific"], [Video, "Google Meet", "Your own private link in your confirmation email"], [ShieldCheck, "Private", "Only you and Gabi see your booking"]].map(([Icon, t, d]) => (
      <li key={t} className="card-surface p-4">
        <Icon size={16} className="text-teal-300 mb-2" aria-hidden="true" />
        <div className="text-neutral-100">{t}</div>
        <div className="text-xs text-neutral-400 mt-0.5">{d}</div>
      </li>
    ))}
  </ul>
);

const Confirmed = ({ info, reset }) => (
  <div className="card-surface p-8 text-center" role="status" aria-live="polite" data-testid="book-confirmed">
    <CheckCircle2 className="mx-auto text-teal-400 mb-4" size={48} aria-hidden="true" />
    <h2 className="font-display text-3xl text-neutral-100 mb-2">You're booked.</h2>
    {info.startTime && (
      <p className="text-neutral-200 mb-2" data-testid="book-confirmed-time">
        {new Date(info.startTime).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
      </p>
    )}
    <p className="text-sm text-neutral-400 max-w-md mx-auto">
      A confirmation email with your private Google Meet link and a calendar invite is on its way. Need to change something? Use the links in that email, or write to{" "}
      <a href={`mailto:${CONTACT}`} className="text-teal-300 hover:text-teal-200">{CONTACT}</a>.
    </p>
    <div className="flex flex-wrap justify-center gap-3 mt-6">
      <Link to="/" className="btn-primary px-6 py-2.5 text-sm" data-testid="book-confirmed-home">Back to homepage</Link>
      <button type="button" onClick={reset} className="rounded-full border border-white/15 px-6 py-2.5 text-sm text-neutral-200 hover:border-white/40" data-testid="book-another">Book another session</button>
    </div>
  </div>
);

export default function BookSession() {
  const [booked, setBooked] = useState(null);
  const onBooked = useCallback((d) => { setBooked(d); window.scrollTo({ top: 0, behavior: "smooth" }); }, []);
  useEffect(() => { document.title = "Book a Session · Why We Recover"; }, []);

  return (
    <div className="min-h-screen" data-testid="book-session-page">
      <header className="max-w-5xl mx-auto px-6 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="book-logo-home" aria-label="Why We Recover home">
          <PhoenixImage variant="mark" size={40} />
          <span className="font-display text-lg text-neutral-100">Why We Recover</span>
        </Link>
        <Link to="/" className="text-sm text-neutral-300 hover:text-teal-200 inline-flex items-center gap-1.5" data-testid="book-back-home">
          <ArrowLeft size={14} aria-hidden="true" /> Home
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-12 pb-24 space-y-10">
        <section className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-3">One-on-one peer support</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-neutral-100" data-testid="book-heading">
            Your Recovery. Your Journey. <span className="gradient-text">Your Space.</span>
          </h1>
          <p className="text-neutral-300 text-base md:text-lg leading-relaxed mt-5" data-testid="book-description">
            Take the next step in your recovery journey with personalized, one-on-one peer support. Why We Recover provides a
            welcoming, judgment-free space to connect, share experiences, and find encouragement.
          </p>
          <p className="text-sm text-neutral-400 mt-4 border-l-2 border-teal-400/50 pl-4" data-testid="book-disclaimer">
            Our peer support sessions are not a substitute for licensed mental health treatment or emergency services.
          </p>
        </section>

        <Facts />

        <section aria-labelledby="pick-time">
          <h2 id="pick-time" className="font-display text-2xl text-neutral-100 mb-4">{booked ? "Confirmation" : "Pick a time that works for you"}</h2>
          {booked ? <Confirmed info={booked} reset={() => setBooked(null)} /> : <CalEmbed onBooked={onBooked} />}
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <div className="card-surface p-5 border-rose-400/30" data-testid="book-crisis">
            <div className="text-sm text-rose-100 inline-flex items-center gap-2 mb-1"><Phone size={14} aria-hidden="true" /> In crisis right now?</div>
            <p className="text-xs text-neutral-400">Please don't wait for a session. Call or text <a href="tel:988" className="text-rose-200 font-semibold">988</a>, any time. If you're in immediate danger, call 911.</p>
          </div>
          <div className="card-surface p-5" data-testid="book-contact">
            <div className="text-sm text-neutral-100 inline-flex items-center gap-2 mb-1"><Mail size={14} className="text-teal-300" aria-hidden="true" /> Questions before you book?</div>
            <a href={`mailto:${CONTACT}`} className="text-sm text-teal-300 hover:text-teal-200" data-testid="book-contact-email">{CONTACT}</a>
          </div>
        </section>
      </main>
    </div>
  );
}
